// A UNIDADE ATIVA DO PORTAL — sobre qual organização as telas estão operando.
//
// Até a feature 009 a resposta era sempre "a do login", e nenhuma tela precisava
// perguntar. Agora um login pode alcançar outras organizações pelas relações
// institucionais, e a pergunta passou a ter mais de uma resposta possível — em
// toda tela, ao mesmo tempo.
//
// TRÊS DECISÕES, E O PORQUÊ DE CADA UMA:
//
//  1. `sessionStorage`, NÃO `localStorage`. A escolha vale para a aba, e morre
//     com ela. Guardar em `localStorage` faria o operador abrir o portal amanhã
//     de manhã já dentro da autarquia, sem ter escolhido — e o próximo arquivo
//     subiria na organização errada. Aba nova começa na origem, sempre.
//  2. A UNIDADE ENTRA NA `queryKey`. Sem isso, trocar de organização mostraria o
//     cache da anterior até o refetch chegar: números de uma, título de outra.
//     `chaveDaUnidade()` é o prefixo que toda query do portal deve carregar.
//  3. QUEM NÃO ESTÁ NA LISTA CAI NA ORIGEM. Id inválido, relação encerrada
//     desde ontem, `sessionStorage` de outro login na mesma aba — em todos os
//     casos a tela volta para a organização do próprio login, em silêncio. O
//     servidor recusaria de qualquer forma (404); avisar aqui só transformaria
//     um estado impossível em erro na cara de quem não fez nada.
//
// O CLIENTE NÃO É A AUTORIZAÇÃO. Este módulo decide o que a tela MOSTRA. Quem
// recusa de verdade é a API — `resolverUnidade`, em `prefeitura/escopo.ts`.

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PrefeituraUnidade } from "@atlas/sdk";
import { atlas } from "./sdk";

const CHAVE = "atlas:unidade";

interface UnidadeAtivaCtx {
  /** Tudo que este login alcança, com a origem primeiro. Vazia enquanto carrega. */
  unidades: PrefeituraUnidade[];
  /** A unidade escolhida — ou a origem, que é o default e o fallback. */
  unidade: PrefeituraUnidade | null;
  /** `undefined` até a lista chegar; depois, o id da unidade ativa. */
  unidadeId: number | undefined;
  /** `true` quando há alguma organização atendida — é o que faz o seletor existir. */
  temAtendidas: boolean;
  carregando: boolean;
  escolher(id: number): void;
  /**
   * O que a unidade ativa acrescenta à `queryKey` de toda query do portal.
   *
   * `["prefeitura", "servidores", ...chaveDaUnidade()]` invalida sozinho ao
   * trocar de organização. Devolve `[]` enquanto a lista não chegou, para a
   * primeira renderização não criar uma chave que muda logo em seguida.
   */
  chaveDaUnidade(): (string | number)[];
  /**
   * O que o SDK precisa receber. `{}` quando a unidade ativa É a origem: assim
   * a request sai idêntica à de antes da feature, e o caminho de sempre
   * continua sendo o caminho de sempre.
   */
  opcoesDoSdk(): { unidadeId?: number };
}

const Ctx = createContext<UnidadeAtivaCtx | null>(null);

function lerEscolha(): number | null {
  try {
    const v = window.sessionStorage.getItem(CHAVE);
    const n = v == null ? NaN : Number(v);
    return Number.isInteger(n) ? n : null;
  } catch {
    // Navegador com storage bloqueado: opera sem memória de escolha, na origem.
    return null;
  }
}

function gravarEscolha(id: number | null): void {
  try {
    if (id == null) window.sessionStorage.removeItem(CHAVE);
    else window.sessionStorage.setItem(CHAVE, String(id));
  } catch {
    /* sem storage, a escolha vale só enquanto a página estiver aberta */
  }
}

export function UnidadeAtivaProvider({ children }: { children: ReactNode }) {
  const q = useQuery({
    queryKey: ["prefeitura", "unidades"],
    queryFn: () => atlas.prefeitura.unidades(),
    // A lista muda quando a averbadora cria ou encerra uma relação — não a cada
    // navegação. Um minuto é curto o bastante para o operador não ficar preso a
    // um escopo velho e longo o bastante para não pesar em cada tela.
    staleTime: 60_000,
  });

  const unidades = useMemo(() => q.data?.unidades ?? [], [q.data]);
  const origem = useMemo(
    // A API já devolve a origem primeiro; o `find` é a garantia de que uma
    // mudança de ordem lá não escolhe a organização errada aqui.
    () => unidades.find((u) => u.origem) ?? unidades[0] ?? null,
    [unidades],
  );

  const [escolhido, setEscolhido] = useState<number | null>(() => lerEscolha());

  // A escolha guardada pode ter deixado de valer entre uma sessão e outra: a
  // relação foi encerrada, ou esta aba trocou de login. Voltar para a origem é
  // o único estado sempre verdadeiro.
  useEffect(() => {
    if (unidades.length === 0) return;
    if (escolhido != null && !unidades.some((u) => u.id === escolhido)) {
      setEscolhido(null);
      gravarEscolha(null);
    }
  }, [unidades, escolhido]);

  const unidade = useMemo(
    () => (escolhido != null ? (unidades.find((u) => u.id === escolhido) ?? origem) : origem),
    [unidades, escolhido, origem],
  );

  const escolher = useCallback(
    (id: number) => {
      setEscolhido(id);
      gravarEscolha(id);
    },
    [],
  );

  const valor = useMemo<UnidadeAtivaCtx>(() => {
    const id = unidade?.id;
    const naOrigem = id != null && origem != null && id === origem.id;
    return {
      unidades,
      unidade,
      unidadeId: id,
      temAtendidas: unidades.length > 1,
      carregando: q.isLoading,
      escolher,
      chaveDaUnidade: () => (id == null ? [] : ["unidade", id]),
      opcoesDoSdk: () => (id == null || naOrigem ? {} : { unidadeId: id }),
    };
  }, [unidades, unidade, origem, q.isLoading, escolher]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

/**
 * A unidade ativa, para as telas do portal.
 *
 * Fora do provider devolve um escopo VAZIO em vez de lançar: as telas do portal
 * são renderizadas em teste isoladas do shell, e obrigar cada uma a montar o
 * provider só para pedir `{}` ao SDK seria cerimônia sem ganho. O
 * comportamento nesse caso é o de antes da feature — sempre a origem.
 */
export function useUnidadeAtiva(): UnidadeAtivaCtx {
  return (
    useContext(Ctx) ?? {
      unidades: [],
      unidade: null,
      unidadeId: undefined,
      temAtendidas: false,
      carregando: false,
      escolher: () => undefined,
      chaveDaUnidade: () => [],
      opcoesDoSdk: () => ({}),
    }
  );
}

/**
 * O rótulo de uma opção do seletor: "A.M.T · processa a folha, administra".
 *
 * Os papéis vão junto porque é o que responde "por que esta opção está aqui?" —
 * e porque eles explicam, sem outra tela, por que um item do menu vai aparecer
 * desabilitado depois de escolhê-la.
 */
/**
 * O NOME DA ORGANIZAÇÃO, e só.
 *
 * Antes trazia a sigla e os papéis (`"A.M.T · processa a folha, administra"`).
 * Saíram os dois, em 08/09/2026, e por motivos diferentes:
 *
 * **A sigla** identifica mal em lista. `nomeExibicao` prefere a sigla porque ela
 * vence ao SE APRESENTAR — na tela do servidor, "A.M.T" é como a autarquia se
 * chama. Mas aqui a pergunta é outra: qual das organizações eu escolho? E
 * "A.M.T" ao lado de "IMAMN" pede que o operador decore siglas para achar a
 * autarquia de trânsito.
 *
 * **Os papéis** eram redundantes com o menu. Ele já desabilita o item que o
 * papel não alcança, com o motivo no hover — que é o lugar onde a informação
 * importa, na hora de clicar. Repeti-la aqui alongava toda opção com um dado
 * que não ajuda a escolher.
 *
 * `(esta organização)` FICA: distinguir a própria da atendida é a única coisa
 * que a lista não diz sozinha, e trocar de unidade sem perceber é o erro caro.
 */
export function rotuloDaUnidade(u: PrefeituraUnidade): string {
  const nome = u.nome || u.sigla || "";
  return u.origem ? `${nome} (esta organização)` : nome;
}
